/**
 * Read the stored go-live checklist for one apartment (COL-529 read-side).
 *
 * The wizard persists Step-3 into apartment_checklist_items (status/source/note,
 * one row per item_key) + apartment_checklist_files (the uploaded proof photos,
 * paths in the private `contracts` bucket). Nothing surfaced that back on the
 * property, so a created property looked empty of everything the owner entered —
 * photos, the Hausverwaltung/caretaker contacts folded into the `contact` note,
 * etc. This hook feeds ApartmentChecklistCard, the read-side that fixes that.
 *
 * Staff read everything (has_any_admin_role); owners read their own via the
 * tables' RLS — so this hook is safe on both the admin and owner detail views.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/** One checklist item row as stored (the shape rowsToChecklist/display need). */
export type ChecklistItemRead = {
  id: string;
  item_key: string;
  status: string;
  source: string | null;
  note: string | null;
};

/** An uploaded proof file, tagged with the item_key it belongs to. */
export type ChecklistFileRead = {
  id: string;
  item_key: string;
  /** Storage path in the private `contracts` bucket — sign with openStoredFile. */
  file_url: string;
  file_name: string | null;
};

export type ApartmentChecklist = {
  items: ChecklistItemRead[];
  files: ChecklistFileRead[];
};

export function useApartmentChecklist(apartmentId: number | null | undefined) {
  return useQuery({
    queryKey: apartmentId
      ? ["v2", "apartment-checklist", apartmentId]
      : ["v2", "apartment-checklist", "noop"],
    queryFn: async (): Promise<ApartmentChecklist> => {
      if (!apartmentId) return { items: [], files: [] };

      const { data: items, error: iErr } = await supabase
        .from("apartment_checklist_items")
        .select("id, item_key, status, source, note")
        .eq("apartment_id", apartmentId);
      if (iErr) throw iErr;
      const rows = (items ?? []) as ChecklistItemRead[];
      if (rows.length === 0) return { items: [], files: [] };

      const byId = new Map(rows.map((r) => [r.id, r.item_key]));
      const { data: files, error: fErr } = await supabase
        .from("apartment_checklist_files")
        .select("id, checklist_item_id, file_url, file_name")
        .in("checklist_item_id", Array.from(byId.keys()));
      if (fErr) throw fErr;

      const fileRows: ChecklistFileRead[] = (files ?? []).map((f) => ({
        id: f.id as string,
        item_key: byId.get(f.checklist_item_id as string) ?? "",
        file_url: f.file_url as string,
        file_name: (f.file_name as string | null) ?? null,
      }));

      return { items: rows, files: fileRows };
    },
    enabled: !!apartmentId,
  });
}
